import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { PublicClientApplication, EventType, type AccountInfo, type AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import msalConfig from '@/config/authConfig';
import App from './App';
import './index.css';

/**
 * Never boot the SPA inside MSAL's hidden authentication iframe (2026-09-07).
 *
 * When a silent renewal is pointed at a URL that serves this app (the SPA
 * root — the pre-fix production config, or any misregistered redirect URI),
 * the iframe loads the whole application. That second
 * PublicClientApplication then runs handleRedirectPromise/acquireTokenSilent
 * of its own, whose preflight throws `block_iframe_reload`, and its
 * resetRequestCache() / setInteractionInProgress(false) write into the SAME
 * sessionStorage the TOP window uses for its pending SIGNIN interaction
 * (MSAL's temporary cache is always sessionStorage, whatever cacheLocation
 * says) — clobbering a redirect the user is in the middle of.
 *
 * The guard fires only when this frame actually carries an auth payload, so a
 * legitimate embedding of the app in an iframe is unaffected.
 */
const AUTH_PAYLOAD_IN_URL = /[?#].*\b(state|code|error)=/;
const isAuthIframe = window.top !== window.self && AUTH_PAYLOAD_IN_URL.test(window.location.href);

function bootstrap(): void {
  // Create MSAL instance
  const msalInstance = new PublicClientApplication(msalConfig);

  // Handle redirect promise for SSO
  msalInstance.initialize().then(() => {
    // Account selection logic
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }

    // Listen for login events.
    // MSAL v5 breaking change: the LOGIN_SUCCESS payload is now an AccountInfo
    // object (v3/v4 delivered an AuthenticationResult with an `account` field).
    // Handle both shapes defensively.
    msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload &&
        typeof event.payload === 'object'
      ) {
        if ('homeAccountId' in event.payload) {
          msalInstance.setActiveAccount(event.payload as AccountInfo);
        } else if ('account' in event.payload) {
          msalInstance.setActiveAccount((event.payload as AuthenticationResult).account ?? null);
        }
      }
    });

    // Render the app
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <ThemeProvider>
            <App />
            <Toaster />
          </ThemeProvider>
        </MsalProvider>
      </StrictMode>
    );
  });
}

if (isAuthIframe) {
  // No React, no second MSAL instance — see the comment above.
  console.warn(
    '[Auth] SPA loaded inside an authentication iframe — not mounting. ' +
      'The redirect URI used for silent renewal must point at /redirect.html.',
  );
} else {
  bootstrap();
}
